import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError, money, clientLabel } from "../lib/api";
import { SearchableSelect } from "./SearchableSelect";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const SourceCard = ({ src, sel, onToggle, onQty }) => (
  <div className="rounded-sm border border-border" data-testid={`cons-src-${src.number}`}>
    <label className="flex items-center gap-2 px-3 py-2 bg-muted/40 cursor-pointer">
      <Checkbox checked={!!sel} onCheckedChange={onToggle} data-testid={`cons-check-${src.number}`} />
      <span className="font-mono text-xs font-bold text-primary">{src.number}</span>
      <Badge variant="outline" className="rounded-sm text-[10px]">{src.type === "quotation" ? "Cotización" : "Envío"}</Badge>
      <span className="text-[11px] text-muted-foreground ml-auto">{src.date} · {src.status}</span>
    </label>
    {sel && (
      <div className="divide-y divide-border">
        {src.items.filter((it) => it.pending > 0).map((it) => (
          <div key={it.index} className="flex items-center gap-2 px-3 py-1.5 text-xs">
            <span className="flex-1 min-w-0 truncate">{it.code && <span className="font-mono mr-1">{it.code}</span>}{it.description}</span>
            <span className="text-muted-foreground whitespace-nowrap">pend. {it.pending}</span>
            <Input type="number" min={0} max={it.pending} step="any" className="h-7 w-20 rounded-sm text-right text-xs"
              value={sel[it.index] ?? 0} onChange={(e) => onQty(it.index, e.target.value)} data-testid={`cons-qty-${src.number}-${it.index}`} />
          </div>
        ))}
      </div>
    )}
  </div>
);

export const ConsolidatedOrderDialog = ({ open, onOpenChange, onSaved }) => {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [sources, setSources] = useState([]);
  const [sel, setSel] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { return; }
    setClientId(""); setSources([]); setSel({});
    api.get("/clients").then((r) => setClients(r.data.filter((c) => !c.historical_only))).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!clientId) { setSources([]); return; }
    setLoading(true); setSel({});
    api.get("/orders/consolidate/sources", { params: { client_id: clientId } })
      .then((r) => setSources(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [clientId]);

  const toggle = (src) => setSel((s) => {
    const n = { ...s };
    if (n[src.id]) delete n[src.id];
    else n[src.id] = Object.fromEntries(src.items.filter((it) => it.pending > 0).map((it) => [it.index, it.pending]));
    return n;
  });
  const setQty = (sid, idx, v) => setSel((s) => ({ ...s, [sid]: { ...s[sid], [idx]: v } }));

  const chosen = sources.filter((s) => sel[s.id]);
  const currency = chosen[0]?.currency || "GTQ";
  const mixed = chosen.some((s) => s.currency !== currency);
  const total = useMemo(() => chosen.reduce((acc, s) => acc + s.items.reduce((a, it) => a + Number(sel[s.id]?.[it.index] || 0) * Number(it.unit_price || 0), 0), 0), [chosen, sel]);

  const save = async () => {
    if (!chosen.length) return toast.error("Selecciona al menos una cotización o envío");
    if (mixed) return toast.error("Todos los documentos deben estar en la misma moneda");
    const payload = { sources: chosen.map((s) => ({ type: s.type, id: s.id, items: Object.entries(sel[s.id]).map(([index, qty]) => ({ index: Number(index), qty: Number(qty || 0) })) })) };
    setSaving(true);
    try {
      const r = await api.post("/orders/consolidate", payload);
      toast.success(`Pedido ${r.data.number} creado`);
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="consolidated-order-dialog">
        <DialogHeader><DialogTitle className="font-heading">Pedido consolidado</DialogTitle>
          <DialogDescription>Jala varias cotizaciones y/o envíos del mismo cliente en un solo pedido. Puedes ajustar cantidades para hacerlo parcial.</DialogDescription></DialogHeader>
        <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-widest">Cliente</Label>
          <SearchableSelect value={clientId} onChange={setClientId} testid="cons-client-select" placeholder="Selecciona un cliente" searchPlaceholder="Buscar cliente..."
            options={clients.map((c) => ({ value: c.id, label: clientLabel(c) }))} /></div>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> :
          clientId && sources.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="cons-empty">Este cliente no tiene cotizaciones ni envíos pendientes.</p> : (
            <div className="space-y-2">{sources.map((s) => (
              <SourceCard key={s.id} src={s} sel={sel[s.id]} onToggle={() => toggle(s)} onQty={(idx, v) => setQty(s.id, idx, v)} />
            ))}</div>)}
        {chosen.length > 0 && (
          <div className="flex justify-between items-center rounded-sm bg-primary/5 px-3 py-2 text-sm" data-testid="cons-summary">
            <span>{chosen.length} documento(s){mixed && <span className="text-destructive ml-2">· monedas distintas</span>}</span>
            <strong className="font-mono">{money(currency, total)}</strong>
          </div>)}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" disabled={saving} onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="rounded-sm font-bold" disabled={saving || !chosen.length} onClick={save} data-testid="cons-save">{saving ? "Creando..." : "Crear pedido"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
